import { apiClient } from './client';
import type { UserRole } from '../types/index.js';

export interface UserAdminItem {
  id: number;
  email: string;
  full_name: string;
  role: UserRole;
  city_id: number | null;
  is_active: boolean;
  created_at: string;
}

export interface UserInviteData {
  email: string;
  full_name: string;
  role: UserRole;
  city_id?: number | null;
}

export interface UserCreateData {
  email: string;
  password: string;
  full_name: string;
  role: UserRole;
  city_id?: number | null;
}

export interface UserInvitationResponse {
  status: string;
  detail: string;
}

export interface UserInvitationItem {
  id: number;
  email: string;
  full_name: string;
  role: UserRole;
  city_id: number | null;
  invited_by_user_id: number | null;
  requested_from_ip: string | null;
  created_at: string;
  expires_at: string;
  accepted_at: string | null;
  revoked_at: string | null;
}

export interface UserUpdateData {
  full_name?: string;
  role?: UserRole;
  is_active?: boolean;
}

export const usersApi = {
  list: async (): Promise<{ items: UserAdminItem[]; total: number }> => {
    const { data } = await apiClient.get('/users/');
    return data;
  },

  get: async (id: number): Promise<UserAdminItem> => {
    const { data } = await apiClient.get(`/users/${id}`);
    return data;
  },

  create: async (payload: UserCreateData): Promise<UserAdminItem> => {
    const { data } = await apiClient.post('/users/', payload);
    return data;
  },

  invite: async (payload: UserInviteData): Promise<UserInvitationResponse> => {
    const { data } = await apiClient.post('/users/invite', payload);
    return data;
  },

  listInvitations: async (): Promise<{ items: UserInvitationItem[]; total: number }> => {
    const { data } = await apiClient.get('/users/invitations');
    return data;
  },

  invalidateInvitation: async (invitationId: number): Promise<UserInvitationResponse> => {
    const { data } = await apiClient.post(`/users/invitations/${invitationId}/invalidate`);
    return data;
  },

  update: async (id: number, payload: UserUpdateData): Promise<UserAdminItem> => {
    const { data } = await apiClient.put(`/users/${id}`, payload);
    return data;
  },

  deactivate: async (id: number): Promise<void> => {
    await apiClient.delete(`/users/${id}`);
  },

  resetPassword: async (id: number, newPassword: string): Promise<void> => {
    await apiClient.post(`/users/${id}/reset-password`, { new_password: newPassword });
  },
};
